/**
 * The widget's side of the postMessage handshake with the customer portal:
 * announces readiness, then takes token, active widget and consent decision
 * from the embedding portal window only.
 */
import { config } from '@this/shared/dist/tools/config.js';
import { logger } from '@this/shared/dist/tools/logger.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { applyConsent } from '../tools/consent.js';
import type { PortalToWidgetMessage, WidgetToPortalMessage } from '../tools/messaging.js';

const ADDIN_ID = 'schadenki';

/**
 * Check if running in iframe (called once outside the hook)
 */
function checkIsInIframe(): boolean {
    try {
        return window.self !== window.top;
    } catch {
        // If we get a security error, we're definitely in an iframe
        return true;
    }
}

// Determine iframe state once at module load
const isInIframe = checkIsInIframe();

/**
 * Hook to handle postMessage communication with the portal.
 *
 * Demo mode is entirely driven by the portal: when the portal is in demo
 * mode it posts `{ type: 'auth-token', payload: { token: 'demo' } }` and
 * the backend's `authUserMiddleware` recognises that literal token and
 * switches to the demo fixtures. The widget itself is agnostic — it just
 * forwards whatever token arrives over the handshake.
 */
export function useMessaging(): {
    getToken: () => Promise<string | null>;
    activeWidget: { id: string | null; seq: number };
    mode: 'standalone' | 'embedded';
} {
    const [activeWidget, setActiveWidget] = useState<{ id: string | null; seq: number }>({ id: null, seq: 0 });
    const tokenRef = useRef<string | null>(null);

    useEffect(() => {
        if (isInIframe && config.app.serviceAddin.portalOrigins.length > 0) {
            // Listen for messages from portal
            const handleMessage = (event: MessageEvent<PortalToWidgetMessage>) => {
                // SECURITY: Validate origin
                if (!config.app.serviceAddin.portalOrigins.includes(event.origin)) {
                    logger.warn('Rejected message from untrusted origin:', event.origin);
                    return;
                }

                // Only the embedding portal window may drive this widget, and a
                // malformed message from an allowed origin (another window, an
                // extension) must not throw inside the listener.
                if (event.source !== window.parent) {
                    return;
                }
                const message = event.data;
                if (typeof message !== 'object' || message === null || typeof message.type !== 'string') {
                    return;
                }

                if (message.type === 'auth-token' && typeof message.payload?.token === 'string') {
                    tokenRef.current = message.payload.token;
                } else if (message.type === 'set-widget' && typeof message.payload?.widgetId === 'string') {
                    setActiveWidget((prev) => ({ id: message.payload.widgetId, seq: prev.seq + 1 }));
                } else if (
                    message.type === 'set-consent' &&
                    ['granted', 'denied', 'unknown'].includes(message.payload?.consent)
                ) {
                    applyConsent(message.payload.consent);
                }
            };

            window.addEventListener('message', handleMessage);

            // Send widget-ready message on mount. The browser silently drops
            // messages whose targetOrigin does not match the actual parent
            // origin, so only the real portal receives it.
            const message: WidgetToPortalMessage = {
                type: 'widget-ready',
                payload: { widget: ADDIN_ID },
            };
            for (const origin of config.app.serviceAddin.portalOrigins) {
                window.parent.postMessage(message, origin);
            }

            // Cleanup
            return () => window.removeEventListener('message', handleMessage);
        }
    }, []);

    // Stable async function to get token
    // Waits infinitely for token when in iframe, returns null immediately in standalone mode
    const getToken = useCallback(async (): Promise<string | null> => {
        // In standalone mode (not in iframe), return null immediately
        if (!isInIframe) {
            return null;
        }

        // If token is already available, return it immediately
        if (tokenRef.current) {
            return tokenRef.current;
        }

        // In iframe mode: wait infinitely for token to arrive
        const checkInterval = 100;
        while (true) {
            if (tokenRef.current) {
                return tokenRef.current;
            }
            await new Promise((resolve) => setTimeout(resolve, checkInterval));
        }
    }, []);

    return {
        getToken,
        activeWidget,
        mode: isInIframe ? ('embedded' as const) : ('standalone' as const),
    };
}
